import type React from "react";
import { useState } from "react";

import { faFileUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { theme } from "theme";

import { ButtonWrapper, InfoTable, StyledParagraph, StyledTableRow, StyledTextArea } from "../shared/styles";

type OnSubmitData = {
  bio: string;
  experience: string;
  availability: string;
  outsideClinic: string;
};

export const ProfileBio: React.FC = () => {
  const { t } = useTranslation();
  const { profile, setProfile } = useProfileContext();
  const [isEditing, setIsEditing] = useState(false);
  const putTherapistMutation = usePutTherapistMutation();

  invariant(profile);

  const { handleSubmit, register, reset } = useForm({
    defaultValues: {
      bio: profile.therapist_profile.bio || "",
      experience: profile.therapist_profile.bio_experience || "",
      availability: profile.therapist_profile.bio_availability || "",
      outsideClinic: profile.therapist_profile.bio_outside_the_clinic || "",
    },
  });

  const handleChangeBioClick = () => {
    putTherapistMutation.reset();
    setIsEditing(!isEditing);
  };

  const onSubmit = async ({ bio, experience, availability, outsideClinic }: OnSubmitData) => {
    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        bio,
        bio_experience: experience,
        bio_availability: availability,
        bio_outside_the_clinic: outsideClinic,
      },
    };

    const variables = { data, therapistId };

    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
        reset({
          bio: data.therapist_profile.bio || "",
          experience: data.therapist_profile.bio_experience || "",
          availability: data.therapist_profile.bio_availability || "",
          outsideClinic: data.therapist_profile.bio_outside_the_clinic || "",
        });
        setIsEditing(!isEditing);
      },
    });
  };

  const { bio, bio_experience, bio_availability, bio_outside_the_clinic } = profile.therapist_profile;

  return (
    <WidgetBox
      data-testid="profile-bio"
      icon={<FontAwesomeIcon icon={faFileUser} />}
      title={t("settings.profile_bio.heading")}
      description={t("settings.profile_bio.description")}
      color="blue"
      toolbar={
        !isEditing ? (
          <BaseButton
            onClick={handleChangeBioClick}
            dataTestID="edit-bio-button"
            variant="action"
            icon="pen"
            text={t("settings.profile_information.change_profile_information")}
            size="large"
          />
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} data-testid="profile-bio">
        <InfoTable $hasFooter={isEditing} data-testid="bio-info">
          {isEditing ? (
            <>
              <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                {t("settings.profile_bio.general_info.heading")}
              </Typography>
              <StyledTextArea dataTestId="bio" {...register("bio")} rows={3} />

              <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                {t("settings.profile_bio.experience.heading")}
              </Typography>
              <StyledTextArea dataTestId="experience" {...register("experience")} rows={3} />

              <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                {t("settings.profile_bio.availability.heading")}
              </Typography>
              <StyledTextArea dataTestId="availability" {...register("availability")} rows={3} />

              <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                {t("settings.profile_bio.hobbies.heading")}
              </Typography>
              <StyledTextArea dataTestId="outsideClinic" {...register("outsideClinic")} rows={3} />

              <ButtonWrapper>
                <BaseButton
                  onClick={handleChangeBioClick}
                  text={t("buttons.cancel")}
                  variant="secondary"
                  size="large"
                />
                <BaseButton type="submit" text={t("buttons.save")} size="large" />
              </ButtonWrapper>
            </>
          ) : (
            <>
              {bio && (
                <StyledTableRow data-testid="bio-info">
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_bio.general_info.heading")}
                  </Typography>
                  <StyledParagraph>{bio}</StyledParagraph>
                </StyledTableRow>
              )}

              {bio_experience && (
                <StyledTableRow data-testid="bio-experience-info">
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_bio.experience.heading")}
                  </Typography>
                  <StyledParagraph>{bio_experience}</StyledParagraph>
                </StyledTableRow>
              )}

              {bio_availability && (
                <StyledTableRow data-testid="bio-availability-info">
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_bio.availability.heading")}
                  </Typography>
                  <StyledParagraph>{bio_availability}</StyledParagraph>
                </StyledTableRow>
              )}

              {bio_outside_the_clinic && (
                <StyledTableRow data-testid="bio-outside-clinic-info">
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_bio.hobbies.heading")}
                  </Typography>
                  <StyledParagraph>{bio_outside_the_clinic}</StyledParagraph>
                </StyledTableRow>
              )}
            </>
          )}
        </InfoTable>

        {putTherapistMutation.isError && (
          <Notification variant="danger" style={{ margin: "10px 0" }}>
            {t("errors.generic")}
          </Notification>
        )}
      </form>
    </WidgetBox>
  );
};
